import React, { ReactNode } from 'react';
import { MST_TGT_TYPES } from '../constants';
import { UtMatrixOption } from '../types';

/**
 * ① 비교 대상 UT Matrix 표 — 기계/전기 탭 공용.
 *
 * 두 탭이 각자 다른 표를 쓰다가(기계=V1/V2 + 체크박스, 전기=전/후 태그 + ✕) 같은 리포트를
 * 보면서 대상 구분을 다르게 부르는 문제가 있어 하나로 통일했다. 컬럼은
 * `구분 / 표시명칭 / UT Matrix No / 버전 / UT Matrix 명 / 상태` 6개로 고정이고,
 * 탭별로 다른 조작 열(기계 행삭제 체크박스, 전기 선택해제 버튼)만 leading/trailing으로 받는다.
 *
 * 구분은 배열 순서에서 그대로 나온다 — `MST_TGT_TYPES`(constants.ts)가 곧 SORT_ORD(1,2,3)이라
 * index 0=기준(MST) / 1=비교대상(TGT) / 2=참조(REF)다. 실시간 분석 경로(selectedMatrices)와
 * 이력 복원 경로(RPT_UTM_M을 SORT_ORD로 정렬해 받음) 모두 이 순서를 지킨다.
 *
 * 전기는 MAX_COMPARE_ELEC=2라 참조 행이 생기지 않고, 기계는 MAX_COMPARE=3이라 참조까지
 * 올 수 있다 — 행 수만 다를 뿐 컬럼 구성은 같다.
 */

/** 구분 태그 색 — 분석 Report 탭의 `.target-badge`(report/styles.ts)와 같은 값을 쓴다 */
const ROLE_TAG_STYLE: Record<string, React.CSSProperties> = {
  MST: { background: '#ecfcf8', color: '#135678', borderColor: '#2d9bb2' },
  TGT: { background: '#fff7e8', color: '#a15c00', borderColor: '#e8a33d' },
  REF: { background: '#f3effc', color: '#554596', borderColor: '#a897df' },
};

/**
 * 셀 정렬/고정열 해제 — 기계 탭(pages/ut/styles.ts)의 분석 테이블 규칙이 셀렉터가 `td`/`thead th`라
 * 같은 스코프의 **모든** 표에 걸린다:
 *   - `td { text-align: right }` + `td:nth-of-type(1~5) { text-align: left }`
 *   - `tbody td:nth-of-type(1~5) { position: sticky; left: 0/60/130/200/280px }`
 * 기존 선택 표가 마침 5컬럼이라 우연히 맞았을 뿐이고, 6컬럼(+조작열)이 되면 UT Matrix 명이
 * 우측으로 밀리고 상태 칸이 고정열에 가려진다. 스코프마다 클래스명이 달라 CSS로 덮을 수 없으므로
 * 셀 단위로 인라인 지정해 덮는다(구분 태그 색과 같은 이유).
 */
const cellBaseStyle: React.CSSProperties = {
  position: 'static',
  background: 'transparent',
};
const leftCellStyle: React.CSSProperties = { ...cellBaseStyle, textAlign: 'left' };
const centerCellStyle: React.CSSProperties = { ...cellBaseStyle, textAlign: 'center' };

/**
 * 헤더는 상단 고정(`position: sticky; top: 0`)은 살리고 가로 고정(`left: 0`)만 푼다 —
 * 첫 5개 th가 전부 left:0이라 그대로 두면 헤더가 서로 겹친다.
 */
const headCellStyle: React.CSSProperties = { left: 'auto', textAlign: 'center' };

/**
 * 기계/전기의 스타일 스코프(styles.ts)가 서로 달라 같은 클래스명을 쓸 수 없어 인라인으로 고정한다
 * (MechUtAnalysisSection의 저장 버튼과 같은 이유).
 */
const roleTagBaseStyle: React.CSSProperties = {
  display: 'inline-block',
  minWidth: 54,
  padding: '1px 8px',
  border: '1px solid transparent',
  borderRadius: 2,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: '18px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

/** 탭별로만 다른 조작 열 (기계=행삭제 체크박스, 전기=선택해제 버튼) */
export interface CompareTargetActionColumn {
  header?: ReactNode;
  width?: number;
  cell: (matrix: UtMatrixOption, index: number) => ReactNode;
}

interface Props {
  /** 배열 순서가 곧 구분 — 0=기준, 1=비교대상, 2=참조 */
  matrices: UtMatrixOption[];
  /** 대상이 비었을 때 표시할 내용 */
  empty: ReactNode;
  /** 빈 행 셀 클래스 — 기계는 `empty-state`, 전기는 `empty`로 스타일이 따로 있다 */
  emptyClassName?: string;
  leading?: CompareTargetActionColumn;
  trailing?: CompareTargetActionColumn;
}

const CompareTargetTable = ({ matrices, empty, emptyClassName, leading, trailing }: Props) => {
  const colSpan = 6 + (leading ? 1 : 0) + (trailing ? 1 : 0);

  return (
    <table>
      <thead>
        <tr>
          {leading && (
            <th style={{ ...headCellStyle, width: leading.width ?? 32 }}>{leading.header}</th>
          )}
          <th style={{ ...headCellStyle, width: 80 }}>구분</th>
          <th style={{ ...headCellStyle, width: 70 }}>표시명칭</th>
          <th style={{ ...headCellStyle, width: 120 }}>UT Matrix No</th>
          <th style={{ ...headCellStyle, width: 70 }}>버전</th>
          <th style={headCellStyle}>UT Matrix 명</th>
          <th style={{ ...headCellStyle, width: 100 }}>상태</th>
          {trailing && (
            <th style={{ ...headCellStyle, width: trailing.width ?? 60 }}>{trailing.header}</th>
          )}
        </tr>
      </thead>
      <tbody>
        {matrices.length === 0 ? (
          <tr>
            <td colSpan={colSpan} className={emptyClassName} style={centerCellStyle}>
              {empty}
            </td>
          </tr>
        ) : (
          matrices.map((m, i) => {
            const role = MST_TGT_TYPES[i];
            return (
              <tr key={m.utmId}>
                {leading && <td style={centerCellStyle}>{leading.cell(m, i)}</td>}
                <td style={centerCellStyle}>
                  {role && (
                    <span style={{ ...roleTagBaseStyle, ...ROLE_TAG_STYLE[role.cd] }}>
                      {role.nm}
                    </span>
                  )}
                </td>
                <td style={centerCellStyle}>V{i + 1}</td>
                <td style={leftCellStyle}>{m.utmNo}</td>
                <td style={centerCellStyle}>{m.verNo}</td>
                <td style={leftCellStyle}>{m.utmNm}</td>
                <td style={centerCellStyle}>{m.statusNm}</td>
                {trailing && <td style={centerCellStyle}>{trailing.cell(m, i)}</td>}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};

export default CompareTargetTable;
